import React, { ReactNode, useCallback, useContext, useState } from 'react';
import styled from 'styled-components';
import usePopup from 'hooks/usePopup';
import NavItem from 'kingpin/navigation/NavItem';

import useContentResults from './hooks/useContentResults';
import useSearch from './hooks/useSearch';
import GLOBAL_SEARCH from './constants';
import useIsEnabled from './hooks/useIsEnabled';
import NoSearchInput from './components/NoSearchInput';
import NoSearchResults from './components/NoSearchResults';
import LoadingSearch from './components/LoadingSearch';
import SearchResults from './components/SearchResults';
import SeeMoreEntityResults from './components/SeeMoreEntityResults';
import SearchBar from './components/SearchBar';
import AnalyticsContext from '../../contexts/AnalyticsContext';
import SearchModal from './SearchModal';

const Scrollable = styled.div`
  max-height: calc(100% - ${GLOBAL_SEARCH.TOP_BAR_HEIGHT}px);
  position: relative;
  overflow-y: auto;
`;

const GlobalSearchContainer = ({
  button,
}: {
  button?: ({ onClick }: { onClick: () => void }) => ReactNode;
}) => {
  const { trackEvent } = useContext(AnalyticsContext);
  const hasEntities = useIsEnabled();
  const { isOpen, open, close } = usePopup();
  const [selectedEntity, setSelectedEntity] = useState<
    EntityDetails.Entity | undefined
  >();

  const onOpened = useCallback(() => {
    trackEvent('Entity Details - Search - Opened');
    open();
  }, [open, trackEvent]);

  const onSearchCancelled = useCallback(() => {
    trackEvent('Entity Details - Search - Cancelled');
    close();
  }, [close, trackEvent]);

  const onEntityNavigate = useCallback(
    (entity: EntityDetails.Entity) => {
      trackEvent('Entity Details - App Opened', {
        entityName: entity.name,
        entityPrimaryField: entity.primaryField,
        source: 'search',
      });
      close();
    },
    [close, trackEvent],
  );

  const onContentNavigate = useCallback(
    (result: GlobalSearch.ContentResult) => {
      trackEvent('Entity Details - Search - Content Opened', {
        type: result.type,
      });
      close();
    },
    [close, trackEvent],
  );

  const {
    searchText,
    onSearchTextChanged,
    isNoResults,
    isLoading,
    isActiveFilterDisabled,
    setIsActiveFilterDisabled,
    results,
  } = useSearch({
    isOpen,
    onSearchCancelled,
    setSelectedEntity,
  });

  const contentGroups = useContentResults({ searchText });
  const isEntitySearchEmpty = !hasEntities || (!isLoading && isNoResults);

  let component: JSX.Element;
  if (searchText === '') {
    component = <NoSearchInput />;
  } else if (selectedEntity && isLoading) {
    component = <LoadingSearch />;
  } else if (selectedEntity) {
    component = (
      <SeeMoreEntityResults
        result={results}
        onEntityNavigate={onEntityNavigate}
        selectedEntity={selectedEntity}
        setSelectedEntity={setSelectedEntity}
        isActiveFilterDisabled={isActiveFilterDisabled}
        setIsActiveFilterDisabled={setIsActiveFilterDisabled}
      />
    );
  } else if (contentGroups.length === 0 && isEntitySearchEmpty) {
    component = (
      <NoSearchResults
        isActiveFilterDisabled={isActiveFilterDisabled}
        setIsActiveFilterDisabled={setIsActiveFilterDisabled}
        searchText={searchText}
      />
    );
  } else {
    component = (
      <SearchResults
        searchText={searchText}
        contentGroups={contentGroups}
        onContentNavigate={onContentNavigate}
        hasEntities={hasEntities}
        isLoadingEntities={isLoading}
        results={results}
        onEntityNavigate={onEntityNavigate}
        setSelectedEntity={setSelectedEntity}
        isActiveFilterDisabled={isActiveFilterDisabled}
        setIsActiveFilterDisabled={setIsActiveFilterDisabled}
      />
    );
  }

  return (
    <>
      <SearchModal isOpen={isOpen} onClose={onSearchCancelled}>
        <SearchBar
          isOpen={isOpen}
          searchText={searchText}
          onSearchTextChanged={onSearchTextChanged}
        />
        <Scrollable>{isOpen && component}</Scrollable>
      </SearchModal>

      {!!button && button({ onClick: onOpened })}
      {!button && (
        <NavItem
          to={'#'}
          label="Search"
          isActiveOverride={false}
          onClick={onOpened}
          icon="search"
          tooltip="Search"
        />
      )}
    </>
  );
};

export default GlobalSearchContainer;
